import type { IndianPokerRngState } from "../types";

const UINT32_SIZE = 4_294_967_296;
const MULBERRY_INCREMENT = 0x6d2b79f5;

function hashSeed(seed: string): number {
  let hash = 2_166_136_261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export function createInitialRngState(seed: string): IndianPokerRngState {
  return {
    seed,
    state: hashSeed(seed),
  };
}

export function nextRandom(rngState: IndianPokerRngState): {
  readonly rngState: IndianPokerRngState;
  readonly value: number;
} {
  const nextState = (rngState.state + MULBERRY_INCREMENT) >>> 0;
  let value = nextState;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);

  return {
    rngState: {
      seed: rngState.seed,
      state: nextState,
    },
    value: ((value ^ (value >>> 14)) >>> 0) / UINT32_SIZE,
  };
}

export function randomInt(
  rngState: IndianPokerRngState,
  maxExclusive: number,
): {
  readonly rngState: IndianPokerRngState;
  readonly value: number;
} {
  if (maxExclusive <= 0) {
    throw new Error("maxExclusive must be positive");
  }

  const result = nextRandom(rngState);
  return {
    rngState: result.rngState,
    value: Math.floor(result.value * maxExclusive),
  };
}

export function shuffleWithRng<T>(
  values: readonly T[],
  rngState: IndianPokerRngState,
): {
  readonly values: readonly T[];
  readonly rngState: IndianPokerRngState;
} {
  const shuffled = [...values];
  let currentRngState = rngState;

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const random = randomInt(currentRngState, index + 1);
    currentRngState = random.rngState;

    const currentValue = shuffled[index];
    const swapValue = shuffled[random.value];
    if (currentValue === undefined || swapValue === undefined) {
      continue;
    }

    shuffled[index] = swapValue;
    shuffled[random.value] = currentValue;
  }

  return {
    values: shuffled,
    rngState: currentRngState,
  };
}
